// Tidio Integration

const TIDIO_API_KEY = process.env.REACT_APP_TIDIO_API_KEY;
const TIDIO_SCRIPT_SRC = TIDIO_API_KEY ? `//code.tidio.co/${TIDIO_API_KEY}.js` : '';
let tidioInitialized = false;
const tidioReadyCallbacks: Array<() => void> = [];

const initializeTidio = () => {
    if (!TIDIO_API_KEY) {
        // [Tidio] REACT_APP_TIDIO_API_KEY is missing. Skipping init.
        return;
    }
    // If script already exists, check if Tidio is ready
    const existingScript =
        document.querySelector(`script[src="${TIDIO_SCRIPT_SRC}"]`) ||
        document.querySelector(`script[src*="code.tidio.co/${TIDIO_API_KEY}.js"]`);
    if (existingScript) {
        // Script exists, check if API is ready
        if (window.tidioChatApi && !tidioInitialized) {
            tidioInitialized = true;
            tidioReadyCallbacks.forEach((cb: () => void) => {
                try {
                    cb();
                } catch {
                    // no-op
                }
            });
            tidioReadyCallbacks.length = 0;
        } else if (!tidioInitialized) {
            // Script exists but API not ready yet, wait for it
            const checkTidioReady = (attempts = 0) => {
                if (window.tidioChatApi) {
                    tidioInitialized = true;
                    tidioReadyCallbacks.forEach((cb) => {
                        try {
                            cb();
                        } catch {
                            // no-op
                        }
                    });
                    tidioReadyCallbacks.length = 0;
                } else if (attempts < 20) {
                    // Retry up to 20 times (2 seconds total)
                    setTimeout(() => checkTidioReady(attempts + 1), 100);
                }
            };
            checkTidioReady();
        }
        return;
    }
    // Script doesn't exist, create and load it
    const script = document.createElement('script');
    script.async = true;
    script.src = TIDIO_SCRIPT_SRC;
    script.onload = () => {
        // Wait a bit for Tidio to fully initialize
        const checkTidioReady = (attempts = 0) => {
            if (window.tidioChatApi) {
                tidioInitialized = true;
                tidioReadyCallbacks.forEach((cb) => {
                    try {
                        cb();
                    } catch {
                        // no-op
                    }
                });
                tidioReadyCallbacks.length = 0;
            } else if (attempts < 20) {
                // Retry up to 20 times (2 seconds total)
                setTimeout(() => checkTidioReady(attempts + 1), 100);
            }
        };
        checkTidioReady();
    };
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
};

export const identifyTidioUser = (
    userId?: string,
    email?: string,
    name?: string,
    phone?: string,
    contactProperties?: Record<string, string | number | boolean>
) => {
    const callIdentify = () => {
        const w = window as unknown as Record<string, any>;
        const tidioChatApi = (w as any).tidioChatApi;
        if (!tidioChatApi) {
            return;
        }
        try {
            // Set visitor data for identification
            if (typeof tidioChatApi.setVisitorData === 'function') {
                const visitorData: { distinct_id?: string; email?: string; name?: string; phone?: string } = {};
                if (userId) {
                    visitorData.distinct_id = userId;
                }
                if (email) {
                    visitorData.email = email;
                }
                if (name) {
                    visitorData.name = name;
                }
                if (phone) {
                    visitorData.phone = phone;
                }
                if (Object.keys(visitorData).length > 0) {
                    tidioChatApi.setVisitorData(visitorData);
                }
            }
            // Set contact properties for additional metadata
            if (contactProperties && typeof tidioChatApi.setContactProperties === 'function') {
                tidioChatApi.setContactProperties(contactProperties);
            }
        } catch {
            // no-op
        }
    };

    // If Tidio is ready, call immediately; otherwise queue it
    if (tidioInitialized && window.tidioChatApi) {
        callIdentify();
    } else {
        tidioReadyCallbacks.push(callIdentify);
        // Also try to initialize if not already done
        if (
            !document.querySelector(`script[src="${TIDIO_SCRIPT_SRC}"]`) &&
            !document.querySelector(`script[src*="code.tidio.co/${TIDIO_API_KEY}.js"]`)
        ) {
            initializeTidio();
        }
    }
};

export default initializeTidio;

export const openTidioChatWithMessage = (message: string) => {
    const callOpenChat = () => {
        const w = window as unknown as Record<string, any>;
        const tidioChatApi = (w as any).tidioChatApi;
        if (!tidioChatApi) {
            // [Tidio] tidioChatApi not available
            return;
        }
        try {
            // Open Tidio chat
            if (typeof tidioChatApi.open === 'function') {
                tidioChatApi.open();
            } else if (typeof tidioChatApi.show === 'function') {
                tidioChatApi.show();
            }
            // Send message after a longer delay to ensure chat is fully open and ready
            setTimeout(() => {
                try {
                    if (typeof tidioChatApi.messageFromOperator === 'function') {
                        tidioChatApi.messageFromVisitor(message);
                    }
                } catch (error) {
                    // no-op
                }
            }, 1000); // Increased delay to 1 second to ensure chat is fully loaded
        } catch (error) {
            // no-op
        }
    };

    // If Tidio is ready, call immediately; otherwise queue it
    if (tidioInitialized && window.tidioChatApi) {
        callOpenChat();
    } else {
        tidioReadyCallbacks.push(callOpenChat);
        // Also try to initialize if not already done
        if (
            !document.querySelector(`script[src="${TIDIO_SCRIPT_SRC}"]`) &&
            !document.querySelector(`script[src*="code.tidio.co/${TIDIO_API_KEY}.js"]`)
        ) {
            initializeTidio();
        }
    }
};

export const destroyTidio = () => {
    const scriptEl =
        document.querySelector(`script[src="${TIDIO_SCRIPT_SRC}"]`) ||
        document.querySelector(`script[src*="code.tidio.co/${TIDIO_API_KEY}.js"]`);
    scriptEl?.parentNode?.removeChild(scriptEl);

    try {
        const w = window as unknown as Record<string, any>;
        if (w.tidioChatApi?.destroy) {
            w.tidioChatApi.destroy();
        }
    } catch {
        // no-op
    }

    try {
        delete (window as any).tidioChatApi;
    } catch {
        // no-op
    }
    tidioInitialized = false;
    tidioReadyCallbacks.length = 0;
};

